import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { useEffect, useId, useState } from 'react';
import useSWR from 'swr';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { prepareUrl } from '@/data/fetcher.tsx';
import { getAuthHeaders } from '@/data/getAuthHeaders';
import { getPlaygroundPath } from '@/utils/constants.tsx';
import { cn } from '@/utils/tailwind';

import {
  ManifestVariant,
  tableVariantLabelClass,
  tableVariantRowClass,
} from './Manifest.utils';

export function ManifestSelectModel(props: {
  variant: ManifestVariant;
  value: string;
  onChange: (value: string) => void;
  models: { label?: string; value: string }[];
  readOnly?: boolean;
  dynamic?: {
    id: string[];
    secrets: Record<string, string> | undefined;
  };
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const id = useId();

  // on render, change the model to props.value (specified by default value)
  useEffect(() => {
    props.onChange(props.value);
  }, []);

  const dynamic = useSWR(
    props.dynamic ? ['ManifestSelectModel', props.dynamic] : null,
    async ([, body]) => {
      const url = prepareUrl(
        `${getPlaygroundPath(false)}/models`,
        new URLSearchParams()
      );
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders()),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to fetch models');
      return (await response.json()) as Array<{ label: string; value: string }>;
    },
    { errorRetryCount: 0 }
  );

  const models = [...props.models];
  for (const model of dynamic.data ?? []) {
    if (!models.find((i) => i.value === model.value)) {
      models.push(model);
    }
  }

  const filtered = models.filter((model) => {
    if (search === '') return true;

    const haystack = (model.label ?? model.value).trim().toLowerCase();
    const needle = search.trim().toLowerCase();
    return haystack.includes(needle);
  });

  const hasExactMatch = filtered.some((model) => model.value === search);

  const modelLabel =
    props.models.find((i) => i.value === props.value)?.label ?? props.value;

  return (
    <div
      className={cn(
        'flex flex-col items-stretch gap-2',
        props.variant === 'table' && tableVariantRowClass
      )}
    >
      <span
        className={cn(
          'my-auto text-sm font-medium',
          props.variant === 'table' && tableVariantLabelClass
        )}
      >
        Model
      </span>

      {props.readOnly ? (
        <div className="overflow-hidden text-ellipsis">
          {props.value == null ? 'No model selected...' : modelLabel}
        </div>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              id={id}
              className="flex items-center gap-2 rounded-md border border-secondary p-1.5 px-2.5 transition-colors hover:bg-secondary-hover active:bg-secondary"
            >
              <span className="flex min-w-0 flex-shrink grow items-center gap-2 overflow-hidden text-ellipsis text-left">
                {props.value == null ? 'Select model...' : modelLabel}
              </span>
              <ChevronDownIcon
                className={cn(
                  'mr-0.5 h-5 w-5 transition-all',
                  open && 'rotate-180'
                )}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="max-h-[50vh] min-w-[375px] overflow-auto p-0"
          >
            <Command shouldFilter={false}>
              <CommandInput
                className="m-2 my-3 mb-0 px-2 py-3"
                value={search}
                onValueChange={setSearch}
                placeholder="Select or type a model..."
              />

              <span className="px-4 pt-2 text-sm text-tertiary">
                If a model isn't listed, you can type it in.
              </span>

              <CommandList>
                <CommandGroup>
                  {dynamic.isLoading && dynamic.data == null && (
                    <span className="flex items-center px-2 py-1 text-tertiary">
                      Loading more models...
                    </span>
                  )}
                  <>
                    {filtered.map((model) => (
                      <CommandItem
                        key={model.value}
                        onSelect={() => {
                          props.onChange(model.value);
                          setOpen(false);
                        }}
                        className="flex items-center gap-2"
                      >
                        {model.label ?? model.value}
                      </CommandItem>
                    ))}
                    {!hasExactMatch && (
                      <CommandItem
                        key={search}
                        onSelect={() => {
                          props.onChange(search);
                          setOpen(false);
                        }}
                        className="flex items-center gap-2"
                      >
                        {search}
                      </CommandItem>
                    )}
                  </>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
